/**
 * @fileoverview TeamWorkloadDetailView - Comprehensive team workload distribution analysis component
 *
 * This component provides detailed insights into how work items (initiatives) are distributed
 * across teams, implementing Option B multi-team assignment logic where initiatives with
 * child epics assigned to multiple teams count once per team.
 *
 * @author Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Divider,
  Breadcrumbs,
  Link,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  ExpandLess,
  Info,
  Group,
  Assignment,
  TrendingUp,
  TrendingDown,
  Remove,
  OpenInNew,
  FilterList,
  Sort
} from '@mui/icons-material';
import { useTeamEnrichedData } from '../hooks/useTeamEnrichedData';
import { useJiraConfiguredQuery } from '../hooks/useJiraConfig';
import ApiService from '../services/apiService';
import { ProcessedInitiative } from '../utils/dataProcessing';
import { filterActiveChildEpics, isActiveStatus } from '../utils/statusColors';
import config from '../config';
import { useTheme } from '../App';
import StandardAppBar from '../components/StandardAppBar';
import StandardBreadcrumbs from '../components/StandardBreadcrumbs';

/**
 * Interface defining the structure of team workload data
 * Used for displaying team-specific initiative assignments and statistics
 */
interface TeamWorkloadData {
  /** Unique team identifier/name */
  teamName: string;
  /** Total count of initiatives assigned to this team */
  initiativeCount: number;
  /** Array of initiatives assigned to this team with detailed information */
  initiatives: Array<{
    /** JIRA issue key (e.g., "RED-12345") */
    key: string;
    /** Initiative title/summary */
    summary: string;
    /** Current status of the initiative */
    status: string;
    /** How the initiative was assigned to this team */
    assignmentType: 'direct' | 'epic';
    /** Assigned user display name */
    assignee?: string;
    /** Initiative start date (ISO string) */
    startDate?: string;
    /** Initiative end date (ISO string) */
    endDate?: string;
  }>;
  /** Sprint information for the team (optional) */
  sprintInfo?: string;
  /** Type/category of the team (optional) */
  teamType?: string;
}

/**
 * Props interface for TeamWorkloadDetailView component
 * Currently no props are required as the component manages its own state
 */
interface TeamWorkloadDetailViewProps {}

/**
 * TeamWorkloadDetailView Component
 *
 * Provides a comprehensive view of team workload distribution with the following features:
 * - Summary statistics (total work items, unique teams, average per team)
 * - Detailed team breakdown with expandable initiative lists
 * - Filtering and sorting capabilities
 * - Calculation methodology explanation
 * - Navigation integration with breadcrumbs
 *
 * @component
 * @example
 * ```tsx
 * <Route path="/team-workload" element={<TeamWorkloadDetailView />} />
 * ```
 */
const TeamWorkloadDetailView: React.FC<TeamWorkloadDetailViewProps> = () => {
  // Navigation hook for programmatic routing
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();

  // Component state management
  /** Set of team names that have their initiative details expanded */
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  /** Current sort field - either by initiative count or team name */
  const [sortBy, setSortBy] = useState<'count' | 'name'>('count');
  /** Sort direction - ascending or descending */
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  /** Workload filter option (currently unused but available for future enhancement) */
  const [filterWorkload, setFilterWorkload] = useState<string>('all');
  /** Search term for filtering teams by name */
  const [searchTerm, setSearchTerm] = useState<string>('');
  /** Whether to show the calculation methodology explanation */
  const [showCalculationHelp, setShowCalculationHelp] = useState<boolean>(false);

  /**
   * Fetch raw initiative data from JIRA API
   * Uses the same query configuration as the Dashboard for consistency
   */
  const {
    data: initiativesResponse,
    isLoading: initiativesLoading
  } = useJiraConfiguredQuery(
    ['red-initiatives'],
    ApiService.getRedInitiatives,
    {
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      retry: false, // Disable React Query retry since API service handles retries
      retryOnMount: false, // Don't retry on component mount
      refetchOnWindowFocus: false, // Don't refetch on window focus
      staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    }
  );

  /**
   * Fetch comprehensive team data with enriched team assignments
   * This provides the team assignment logic and processing
   */
  const {
    data: comprehensiveTeamData,
    isLoading: teamDataLoading
  } = useTeamEnrichedData({
    enabled: true,
    selectedTeam: undefined
  });

  /** Combined loading state for both data sources */
  const isLoading = initiativesLoading || teamDataLoading;

  /**
   * Get teams from active child epics only (filtering out closed/done epics)
   *
   * Uses the enhanced backend data structure that includes child epic status information
   * to filter out inactive epics before determining team assignments.
   *
   * @param enrichedInitiative - Initiative with team enrichment data including child epic statuses
   * @returns Filtered array of team names from active child epics only
   */
  const getActiveChildEpicTeams = (enrichedInitiative: any): string[] => {
    const childEpicsWithStatus = enrichedInitiative.childEpicsWithStatus || [];

    // If we have the new status data structure, use it for filtering
    if (childEpicsWithStatus.length > 0) {
      const activeChildEpics = filterActiveChildEpics(childEpicsWithStatus);
      const activeTeams = [...new Set(activeChildEpics.map(epic => epic.team).filter(team => team !== 'Unknown'))];

      if (config.debug) {
        console.log(`✅ Child epic status filtering active for ${enrichedInitiative.key}:`, {
          totalChildEpics: childEpicsWithStatus.length,
          activeChildEpics: activeChildEpics.length,
          filteredTeams: activeTeams
        });
      }

      return activeTeams;
    }

    // Fallback to original behavior if new data structure isn't available
    const childEpicTeams = enrichedInitiative.childEpicTeams || [];
    if (childEpicTeams.length > 0) {
      console.warn(`⚠️ Child epic status filtering not fully implemented for ${enrichedInitiative.key}. Using all child epic teams.`);
    }

    return childEpicTeams;
  };

  /**
   * Calculate team workload data using Option B multi-team assignment logic
   *
   * This is the core calculation that implements the business logic for team workload distribution:
   * 1. Filters initiatives to only include "In Progress" status
   * 2. Uses enriched team data to get both direct and epic-level team assignments
   * 3. Applies child epic status filtering to exclude completed/closed epics from team assignments
   * 4. Counts each initiative once per team when multiple teams are involved
   * 5. Calculates summary statistics for the overview
   *
   * Child epic status filtering: Now fully implemented using backend status data.
   * Only active child epics (not Done/Closed/Resolved/Cancelled) contribute to team assignments.
   *
   * @returns Object containing team workload data and summary statistics
   */
  const teamWorkloadData = useMemo(() => {
    const initiatives = initiativesResponse?.data;

    // Early return if data is not yet available
    if (!initiatives || !comprehensiveTeamData || isLoading) {
      return {
        teams: [],
        summary: {
          totalWorkItems: 0,
          uniqueTeams: 0,
          avgWorkItemsPerTeam: 0,
          inProgressInitiatives: 0
        }
      };
    }

    /**
     * Filter initiatives to only include those with "In Progress" status
     * This ensures we only count active work items in the workload calculation
     */
    const inProgressInitiatives = initiatives.filter(initiative => {
      const status = typeof initiative.status === 'string'
        ? initiative.status
        : (initiative.status?.name || '');
      return status.toLowerCase().includes('progress');
    });

    /**
     * Create a lookup map for enriched initiative data
     * This provides access to team assignments from both direct and epic-level sources
     */
    const comprehensiveDataMap = new Map();
    comprehensiveTeamData.forEach(item => {
      comprehensiveDataMap.set(item.key, item);
    });

    /**
     * Team workload tracking structure
     * Maps team names to their work counts and detailed initiative information
     */
    const teamWorkCounts = new Map<string, {
      count: number;
      initiatives: Array<{
        key: string;
        summary: string;
        status: string;
        assignmentType: 'direct' | 'epic';
        assignee?: string;
        startDate?: string;
        endDate?: string;
      }>;
      sprintInfo?: string;
    }>();

    // Counters for summary statistics
    let totalWorkItems = 0;
    let clusterInterfaceCount = 0;
    const clusterInterfaceInitiatives: string[] = [];

    inProgressInitiatives.forEach(initiative => {
      const enrichedInitiative = comprehensiveDataMap.get(initiative.key) || initiative;
      const initiativeTeams = new Set<string>();

      // Get teams from enriched data, filtering for active child epics
      // Uses enhanced backend data with child epic status information for accurate filtering
      let teams = enrichedInitiative.teams || [];

      // If this initiative has child epics, filter by active status
      if (enrichedInitiative.childEpicTeams && enrichedInitiative.childEpicTeams.length > 0) {
        // Apply child epic status filtering to exclude completed/closed epics
        const activeChildEpicTeams = getActiveChildEpicTeams(enrichedInitiative);

        // If we have child epic teams, use them; otherwise fall back to initiative-level team
        if (activeChildEpicTeams.length > 0) {
          teams = activeChildEpicTeams;
        } else if (enrichedInitiative.team && enrichedInitiative.team !== 'Unknown') {
          // All child epics are inactive, fall back to initiative-level assignment
          teams = [enrichedInitiative.team];
        } else {
          // No active child epics and no initiative-level team
          teams = [];
        }
      }

      teams.forEach((team: string) => {
        if (team && team !== 'Unknown' && !initiativeTeams.has(team)) {
          if (!teamWorkCounts.has(team)) {
            teamWorkCounts.set(team, {
              count: 0,
              initiatives: [],
              sprintInfo: enrichedInitiative.teamSprint || ''
            });
          }

          const teamData = teamWorkCounts.get(team)!;
          teamData.count += 1;

          if (team === 'Cluster Interface') {
            clusterInterfaceCount++;
            clusterInterfaceInitiatives.push(initiative.key);
          }
          
          // Determine assignment type
          const hasChildEpics = enrichedInitiative.childEpicTeams && 
                               enrichedInitiative.childEpicTeams.length > 0;
          const assignmentType = hasChildEpics ? 'epic' : 'direct';

          teamData.initiatives.push({
            key: initiative.key,
            summary: initiative.summary,
            status: typeof initiative.status === 'string' 
              ? initiative.status 
              : (initiative.status?.name || ''),
            assignmentType,
            assignee: initiative.assignee?.displayName,
            startDate: initiative.customFields?.startDate,
            endDate: initiative.customFields?.endDate
          });

          totalWorkItems++;
          initiativeTeams.add(team);
        }
      });
    });

    // Convert to array format
    const teamsArray: TeamWorkloadData[] = Array.from(teamWorkCounts.entries()).map(([teamName, data]) => ({
      teamName,
      initiativeCount: data.count,
      initiatives: data.initiatives,
      sprintInfo: data.sprintInfo,
      teamType: data.sprintInfo ? 'Sprint Team' : 'Other'
    }));

    return {
      teams: teamsArray,
      summary: {
        totalWorkItems,
        uniqueTeams: teamsArray.length,
        avgWorkItemsPerTeam: teamsArray.length > 0 ? totalWorkItems / teamsArray.length : 0,
        inProgressInitiatives: inProgressInitiatives.length
      }
    };
  }, [initiativesResponse, comprehensiveTeamData, isLoading]);

  // Filter and sort teams
  const filteredAndSortedTeams = useMemo(() => {
    let filtered = teamWorkloadData.teams;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(team =>
        team.teamName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }



    // Apply workload filter
    if (filterWorkload !== 'all') {
      const avgWorkload = teamWorkloadData.summary.avgWorkItemsPerTeam;
      filtered = filtered.filter(team => {
        if (filterWorkload === 'above') return team.initiativeCount > avgWorkload;
        if (filterWorkload === 'below') return team.initiativeCount < avgWorkload;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'count') {
        comparison = a.initiativeCount - b.initiativeCount;
      } else {
        comparison = a.teamName.localeCompare(b.teamName);
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [teamWorkloadData.teams, searchTerm, filterWorkload, sortBy, sortOrder, teamWorkloadData.summary.avgWorkItemsPerTeam]);

  /**
   * Toggle the expansion state of a team row to show/hide initiative details
   *
   * @param teamName - The name of the team to toggle expansion for
   */
  const toggleTeamExpansion = (teamName: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamName)) {
      newExpanded.delete(teamName);
    } else {
      newExpanded.add(teamName);
    }
    setExpandedTeams(newExpanded);
  };

  /**
   * Get visual indicator (color and icon) for team workload relative to average
   *
   * @param count - Number of work items for the team
   * @param avgWorkload - Average work items across all teams
   * @returns Object with color and icon for visual indication
   */
  const getWorkloadIndicator = (count: number, avgWorkload: number) => {
    if (count > avgWorkload * 1.5) return { color: 'error.main', icon: <TrendingUp /> };
    if (count > avgWorkload) return { color: 'warning.main', icon: <TrendingUp /> };
    if (count < avgWorkload * 0.5) return { color: 'info.main', icon: <TrendingDown /> };
    return { color: 'success.main', icon: <Remove /> };
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <>
      <StandardAppBar
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />
      <Container maxWidth="lg" sx={{ mt: 2, mb: 2, pb: 8 }} data-testid="team-workload-container">
        {/* Header */}
        <Box sx={{ mb: 3 }} data-testid="page-header">
          {/* Navigation */}
          <StandardBreadcrumbs
            currentPage="Team Workload Distribution"
          />

        {/* Title and Summary */}
        <Box sx={{ mb: 3 }} data-testid="summary-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }} data-testid="page-title">
            Active Initiatives by Team
          </Typography>

          {isLoading ? (
            <Box sx={{ mb: 2 }} data-testid="loading-state">
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading team workload data...
              </Typography>
            </Box>
          ) : (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }} data-testid="summary-statistics">
              {teamWorkloadData.summary.totalWorkItems} work items across{' '}
              {teamWorkloadData.summary.uniqueTeams} teams{' '}
              ({teamWorkloadData.summary.avgWorkItemsPerTeam.toFixed(1)} avg per team)
            </Typography>
          )}
        </Box>
      </Box>

      {/* Calculation Guidelines */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'info.main' }} data-testid="calculation-logic-section">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="info" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} data-testid="calculation-logic-title">
                Calculation Logic
              </Typography>
            </Box>
            <Button
              onClick={() => setShowCalculationHelp(!showCalculationHelp)}
              endIcon={showCalculationHelp ? <ExpandLess /> : <ExpandMore />}
              size="small"
              data-testid="calculation-logic-toggle"
            >
              {showCalculationHelp ? 'Hide Details' : 'Show Details'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Only "In Progress" initiatives</strong> are counted in this calculation
            </Typography>
            
            <Collapse in={showCalculationHelp}>
              <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'info.light' }} data-testid="calculation-logic-details">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>For initiatives without child epics:</strong> Count as 1 assignment to the team/sprint the initiative is assigned to
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>For initiatives with child epics:</strong>
                </Typography>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    - <strong>Filter child epics by status:</strong> Only include active epics (excludes Done, Closed, Resolved, Cancelled)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    - <strong>Count unique teams:</strong> Each team with active child epics gets 1 assignment per initiative
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    - <strong>Avoid double-counting:</strong> Multiple active epics from the same team = 1 assignment
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                    - <strong>Fallback:</strong> If all child epics are closed, use initiative-level team assignment
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {/* Filters and Controls */}
      {!isLoading && (
        <Card sx={{ mb: 3 }} data-testid="filters-section">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FilterList color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Filters & Sorting
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search teams"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type team name..."
                  data-testid="search-teams-input"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort by"
                    onChange={(e) => setSortBy(e.target.value as 'count' | 'name')}
                    data-testid="sort-by-select"
                  >
                    <MenuItem value="count">Initiative Count</MenuItem>
                    <MenuItem value="name">Team Name</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Order</InputLabel>
                  <Select
                    value={sortOrder}
                    label="Order"
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  >
                    <MenuItem value="desc">High to Low</MenuItem>
                    <MenuItem value="asc">Low to High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Workload</InputLabel>
                  <Select
                    value={filterWorkload}
                    label="Workload"
                    onChange={(e) => setFilterWorkload(e.target.value)}
                  >
                    <MenuItem value="all">All Teams</MenuItem>
                    <MenuItem value="above">Above Average</MenuItem>
                    <MenuItem value="below">Below Average</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Team List */}
      {!isLoading && (
        <Card data-testid="team-table-card">
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table data-testid="team-table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}></TableCell>
                    <TableCell data-testid="team-name-header">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Group />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Team
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" data-testid="work-items-header">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Assignment />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Work Items
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" data-testid="workload-header">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Workload
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedTeams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No teams found matching the current filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAndSortedTeams.map((team) => {
                      const isExpanded = expandedTeams.has(team.teamName);
                      const workloadIndicator = getWorkloadIndicator(
                        team.initiativeCount,
                        teamWorkloadData.summary.avgWorkItemsPerTeam
                      );

                      return (
                        <React.Fragment key={team.teamName}>
                          {/* Team Row */}
                          <TableRow
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleTeamExpansion(team.teamName)}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={team.teamName}
                                variant="outlined"
                                size="small"
                                sx={{ maxWidth: 200 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                {team.initiativeCount}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Box sx={{ color: workloadIndicator.color }}>
                                  {workloadIndicator.icon}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: workloadIndicator.color, fontWeight: 'medium' }}
                                >
                                  {team.initiativeCount > teamWorkloadData.summary.avgWorkItemsPerTeam ? 'Above' :
                                   team.initiativeCount < teamWorkloadData.summary.avgWorkItemsPerTeam ? 'Below' : 'Average'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expanded Initiative Details */}
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{
                                  p: 2,
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50',
                                  borderRadius: 1,
                                  m: 1,
                                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                }}>
                                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    Initiatives assigned to {team.teamName} ({team.initiatives.length})
                                  </Typography>

                                  <Grid container spacing={1}>
                                    {team.initiatives.map((initiative) => (
                                      <Grid item xs={12} key={initiative.key}>
                                        <Card variant="outlined" sx={{
                                          p: 1.5,
                                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'background.paper',
                                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider'
                                        }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Chip
                                              icon={initiative.assignmentType === 'direct' ?
                                                <span style={{ fontSize: '12px' }}>🔵</span> :
                                                <span style={{ fontSize: '12px' }}>🟡</span>
                                              }
                                              label={initiative.assignmentType === 'direct' ? 'Direct Assignment' : 'Child Epic Assignment'}
                                              size="small"
                                              variant="outlined"
                                            />
                                            <Chip
                                              label={initiative.status}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          </Box>

                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography
                                              variant="body2"
                                              color="primary"
                                              sx={{ fontWeight: 'medium', cursor: 'pointer' }}
                                              onClick={() => navigate(`/initiative/${initiative.key}`)}
                                            >
                                              {initiative.key}
                                            </Typography>
                                            <Tooltip title="Open in JIRA">
                                              <IconButton
                                                size="small"
                                                onClick={() => window.open(`https://redislabs.atlassian.net/browse/${initiative.key}`, '_blank')}
                                              >
                                                <OpenInNew sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>

                                          <Typography variant="body2" sx={{ mb: 1 }}>
                                            {initiative.summary}
                                          </Typography>

                                          {(initiative.assignee || initiative.startDate || initiative.endDate) && (
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                              {initiative.assignee && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Assignee: {initiative.assignee}
                                                </Typography>
                                              )}
                                              {initiative.startDate && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Start: {initiative.startDate}
                                                </Typography>
                                              )}
                                              {initiative.endDate && (
                                                <Typography variant="caption" color="text.secondary">
                                                  End: {initiative.endDate}
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Card>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {!isLoading && filteredAndSortedTeams.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Summary Statistics
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.2)' : 'primary.light',
                  borderRadius: 1,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(33, 150, 243, 0.3)' : 'none'
                }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 'bold',
                    color: (theme) => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.contrastText'
                  }}>
                    {filteredAndSortedTeams.length}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.contrastText'
                  }}>
                    Teams Shown
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'success.light',
                  borderRadius: 1,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(76, 175, 80, 0.3)' : 'none'
                }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 'bold',
                    color: (theme) => theme.palette.mode === 'dark' ? 'success.light' : 'success.contrastText'
                  }}>
                    {filteredAndSortedTeams.reduce((sum, team) => sum + team.initiativeCount, 0)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? 'success.light' : 'success.contrastText'
                  }}>
                    Total Work Items
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'warning.light',
                  borderRadius: 1,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 152, 0, 0.3)' : 'none'
                }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 'bold',
                    color: (theme) => theme.palette.mode === 'dark' ? 'warning.light' : 'warning.contrastText'
                  }}>
                    {filteredAndSortedTeams.filter(team => team.initiativeCount > teamWorkloadData.summary.avgWorkItemsPerTeam).length}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? 'warning.light' : 'warning.contrastText'
                  }}>
                    Above Average
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.2)' : 'info.light',
                  borderRadius: 1,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(33, 150, 243, 0.3)' : 'none'
                }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 'bold',
                    color: (theme) => theme.palette.mode === 'dark' ? 'info.light' : 'info.contrastText'
                  }}>
                    {filteredAndSortedTeams.filter(team => team.initiativeCount < teamWorkloadData.summary.avgWorkItemsPerTeam).length}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? 'info.light' : 'info.contrastText'
                  }}>
                    Below Average
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading team workload data...
            </Typography>
          </CardContent>
        </Card>
      )}
      </Container>
    </>
  );
};

export default TeamWorkloadDetailView;
